import React, { useState, useEffect } from 'react';
import { Container } from '@mantine/core';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { IoArrowBackSharp, IoInformationCircleOutline } from 'react-icons/io5';
import { HiOutlineDocumentMagnifyingGlass } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import classNames from 'classnames';

// Custom Components
import Loader from '../../components/Loader/Loader';
import PageTitle from '../../components/PageTitle/PageTitle';
import Footer from '../../components/Footer/Footer';

// API-Service
import { facilitiesListService } from '../../service/api.service';

// Utility Service
import { getCurrencyText } from '../../helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Pastel palette
// Each entry has two stops:
//   zone → full image-zone background (very light)
//   wrap → inner rounded-square background (slightly deeper)
// Colour is deterministic: same name always maps to the same pair.
// ─────────────────────────────────────────────────────────────────────────────
const PASTEL_PAIRS = [
    { zone: '#fff3ee', wrap: '#fde3d0' }, // peach
    { zone: '#eef6fb', wrap: '#d0eaf8' }, // sky blue
    { zone: '#edfaf3', wrap: '#c8f0dc' }, // mint green
    { zone: '#f8f0fd', wrap: '#edd8f8' }, // lavender
    { zone: '#fef0f2', wrap: '#fcd4da' }, // rose pink
    { zone: '#fffbee', wrap: '#fef0be' }, // pale yellow
    { zone: '#eef3fd', wrap: '#d0dff8' }, // cornflower blue
    { zone: '#edf9f7', wrap: '#c4edea' }, // teal
];

const getPastelPair = (name = '') => {
    const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return PASTEL_PAIRS[hash % PASTEL_PAIRS.length];
};

const getInitials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');

// ─────────────────────────────────────────────────────────────────────────────
// SportImageZone
//
// Pixel-matches the reference card design:
//   • Outer div (.sport-card__img-zone)  — full card width, very light pastel bg
//   • Inner div (.sport-card__icon-wrap) — 90×90 rounded square, deeper pastel bg
//       └ <img>    when a valid URL is supplied
//       └ initials when image is null or fails to load
// ─────────────────────────────────────────────────────────────────────────────
const SportImageZone = ({ image, name, disabled }) => {
    const [imgError, setImgError] = useState(false);
    const hasImage = image && !imgError;
    const { zone, wrap } = getPastelPair(name);

    return (
        <div
            className="sport-card__img-zone"
            style={{ backgroundColor: zone }}
        >
            <div
                className={classNames('sport-card__icon-wrap', {
                    'sport-card__icon-wrap--disabled': disabled,
                })}
                style={{ backgroundColor: wrap }}
            >
                {hasImage ? (
                    <img
                        className="sport-card__img"
                        src={image}
                        alt={name}
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <span className="sport-card__initials">
                        {getInitials(name)}
                    </span>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// EmptyState
//
// Shown when the machine UUID is missing/invalid, or when the UUID is valid
// but returns zero sports. Mirrors the "not found" illustration pattern:
// icon → heading → description → detail badge → primary action.
// ─────────────────────────────────────────────────────────────────────────────
const EmptyState = ({ onGoBack }) => (
    <div className="empty-state">
        <div className="empty-state__icon-wrap">
            <HiOutlineDocumentMagnifyingGlass className="empty-state__icon" />
            <span className="empty-state__sparkle empty-state__sparkle--tl" />
            <span className="empty-state__sparkle empty-state__sparkle--tr" />
            <span className="empty-state__sparkle empty-state__sparkle--bl" />
        </div>

        

        <p className="empty-state__desc">
            We couldn&apos;t find the data you&apos;re looking for.
            <br />
            It might be removed or the link may be incorrect.
        </p>

        <div className="empty-state__badge">
            <IoInformationCircleOutline className="empty-state__badge-icon" />
            <span>Empty/Invalid UUID</span>
        </div>

        <style>{`
            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                margin: auto;
                padding: 30px 24px;
                max-width: 420px;
            }

            .empty-state__icon-wrap {
                position: relative;
                width: 96px;
                height: 96px;
                margin-bottom: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .empty-state__icon {
                width: 100%;
                height: 100%;
                color: #FA5000;
                stroke-width: 1.4;
            }

            .empty-state__sparkle {
                position: absolute;
                width: 6px;
                height: 6px;
                border-radius: 2px;
                background: #f7a8b8;
                transform: rotate(45deg);
            }
            .empty-state__sparkle--tl { top: 2px; left: -6px; background: #f7a8b8; }
            .empty-state__sparkle--tr { top: 18px; right: -10px; width: 4px; height: 4px; background: #c9c1f7; }
            .empty-state__sparkle--bl { bottom: 8px; left: -4px; width: 4px; height: 4px; background: #c9c1f7; }

            .empty-state__title {
                font-size: 28px;
                font-weight: 700;
                color: #14142b;
                margin: 0 0 12px;
            }

            .empty-state__desc {
                font-size: 15px;
                line-height: 1.6;
                color: #6b7280;
                margin: 0 0 24px;
            }

            .empty-state__badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                background: #f4f2fd;
                color: #FA5000;
                font-weight: 600;
                font-size: 14px;
                padding: 12px 20px;
                border-radius: 10px;
                margin-bottom: 24px;
                width: 100%;
                justify-content: center;
            }

            .empty-state__badge-icon {
                font-size: 18px;
                flex-shrink: 0;
            }

            .empty-state__back-btn {
                display: inline-flex;
                align-items: center;
                gap: 10px;
                background: #FA5000;
                color: #fff;
                border: none;
                border-radius: 10px;
                padding: 12px 28px;
                font-size: 15px;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.15s ease, transform 0.1s ease;
            }

            .empty-state__back-btn:hover {
                background: #FA5000;
            }

            .empty-state__back-btn:active {
                transform: scale(0.98);
            }

            .empty-state__back-icon {
                font-size: 16px;
            }
        `}</style>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// getSportsFromFacilities
// Groups facilities by root sport category (parent_id === null).
// ─────────────────────────────────────────────────────────────────────────────
const getSportsFromFacilities = (facilities) => {
    const sportsMap = new Map();

    facilities.forEach((facility) => {
        if (!facility.categories || facility.categories.length === 0) return;

        const rootCategory =
            facility.categories.find((c) => c.category.parent_id === null)?.category ||
            facility.categories[0]?.category;

        if (!rootCategory) return;

        const sportId = rootCategory.id;

        if (!sportsMap.has(sportId)) {
            sportsMap.set(sportId, {
                sportId,
                name: rootCategory.name,
                image: rootCategory.images?.[0] || null,
                description: rootCategory.description || '',
                facilities: [],
                hasActiveFacility: false,
            });
        }

        const sport = sportsMap.get(sportId);
        sport.facilities.push(facility);
        if (facility.status === 1) sport.hasActiveFacility = true;
    });

    return Array.from(sportsMap.values()).sort((a, b) => {
        const seqA = a.facilities[0]?.user_sequence ?? 999;
        const seqB = b.facilities[0]?.user_sequence ?? 999;
        return seqA - seqB;
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// Facilities Page
// ─────────────────────────────────────────────────────────────────────────────
const Facilities = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [complexImage, setComplexImage] = useState(localStorage.getItem('complexImage') || '');
    const [complexName, setComplexName] = useState(localStorage.getItem('complexName') || '');
    const [complexLocation, setComplexLocation] = useState(localStorage.getItem('complexLocation') || '');
    const [complexTimeZone, setComplexTimeZone] = useState(localStorage.getItem('complexTimeZone') || '');
    const [policy, setPolicy] = useState('');
    const [facilities, setFacilities] = useState([]);
    const [sports, setSports] = useState([]);
    const [currency, setCurrency] = useState('MYR');

    let uuid = '';
    if (searchParams?.get('machineUUID')) {
        uuid = searchParams?.get('machineUUID') || location?.state?.uuid;
    } else if (searchParams?.get('auth')) {
        uuid = searchParams?.get('auth') || location?.state?.uuid;
    } else {
        uuid = location?.state?.uuid;
    }

    localStorage.setItem('machineUUID', uuid);
    localStorage.setItem('isAuth', !!searchParams?.get('auth'));
    const isAuth = localStorage.getItem('isAuth' || false);

    const fetchFacilities = async () => {
        try {
            const result = await facilitiesListService();
            if (result?.data?.success === 1) {
                setLoading(false);
                const facilitiesData = result?.data?.data?.sports_facilities;
                setFacilities(facilitiesData);
                setSports(getSportsFromFacilities(facilitiesData));
                const imageArray = result?.data?.data?.sports_complex.images;
                setComplexName(`${result?.data?.data?.sports_complex.name}`)
                setComplexLocation(`${result?.data?.data?.sports_complex.info.address}`)
                setComplexTimeZone(`${result?.data?.data?.sports_complex.info.timezone_override}`)
                setCurrency(getCurrencyText(result?.data?.data?.sports_complex?.info?.country));
                setComplexImage(`${imageArray[0]}`);
                setPolicy(`${result?.data?.data?.sports_complex?.info?.center_policy}`);
                localStorage.setItem('complexName', result?.data?.data?.sports_complex.name);
                localStorage.setItem('complexLocation', result?.data?.data?.sports_complex.info.address);
                localStorage.setItem('complexTimeZone', result?.data?.data?.sports_complex.info.timezone_override);
                localStorage.setItem('complexImage', `${imageArray[0]}`);

            } else {
                setLoading(false);
                Swal.fire({
                    title: 'Error!',
                    text: result?.data?.message || result?.response?.data?.message || 'Something went wrong',
                    icon: 'error',
                    confirmButtonText: 'Try Again',
                    confirmButtonColor: '#FA5000',
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: error || error?.response?.data?.message || 'Something went wrong',
                icon: 'error',
                confirmButtonText: 'Try Again',
                confirmButtonColor: '#FA5000',
            });
        }
    };

    const handleGoBack = () => {
        if (window?.kioskVEHome) {
            window.kioskVEHome();
        } else {
            window.history.back();
        }
    };

    useEffect(() => {
        if (searchParams?.get('machineUUID') || searchParams?.get('auth')) {
            fetchFacilities();
        }
        const timeoutId = setTimeout(() => 3000);
        return function cleanup() {
            clearTimeout(timeoutId);
        };
    }, []);

    const showEmptyState = !uuid || (!loading && sports?.length === 0);

    return (
        <Container fluid className="px-0 min-vh-100 d-flex flex-column" size="md">
            <PageTitle uuid={uuid} title="Types of Sports" shortHead="Explore & Play" logo={complexImage} name={complexName} location={complexLocation} />

            {showEmptyState && <EmptyState onGoBack={handleGoBack} />}

            {!isAuth && !showEmptyState && (
                <IoArrowBackSharp
                    role="button"
                    size={30}
                    className="text-dark"
                    onClick={handleGoBack}
                />
            )}

            {!showEmptyState && (uuid && loading ? (
                <Loader />
            ) : (
                <Container fluid className="w-100 px-3 px-sm-5">
                    <div className="sports-grid">
                        {sports?.map((sport) => {
                            const primaryFacility =
                                sport.facilities.find((f) => f.status === 1) || sport.facilities[0];
                            const isBookable = sport.hasActiveFacility;

                            const linkState = {
                                facilitiesTitle: sport.name,
                                facilities,
                                courts: primaryFacility?.courts,
                                timeSlots: primaryFacility?.timeSlots || [],
                                id: primaryFacility?.id,
                                uuid,
                                is30MinAllowed: primaryFacility?.booking_window === '30.0',
                                timings: primaryFacility?.timings,
                                minBookingHours: Number(primaryFacility?.min_booking_hour),
                                currency,
                                complexImage,
                                maxBookingDays: primaryFacility?.max_booking_days,
                                policy,
                                sportFacilities: sport.facilities,
                            };

                            const toPath = isBookable ? '/scheduler' : `?machineUUID=${uuid}`;

                            return (
                                <div
                                    key={sport.sportId}
                                    className={classNames('sport-card', {
                                        'sport-card--disabled': !isBookable,
                                    })}
                                >
                                    {/* Image zone: light pastel outer + deeper pastel inner rounded square */}
                                    <SportImageZone
                                        image={sport.image}
                                        name={sport.name}
                                        disabled={!isBookable}
                                    />

                                    {/* Card body */}
                                    <div className="sport-card__body">
                                        <p className="sport-card__name">{sport.name}</p>



                                        <Link
                                            className={classNames('sport-card__btn', {
                                                'sport-card__btn--disabled': !isBookable,
                                            })}
                                            to={toPath}
                                            state={linkState}
                                            onClick={(e) => { if (!isBookable) e.preventDefault(); }}
                                        >
                                            {isBookable ? 'Book Now' : 'Unavailable'}
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Container>
            ))}

            <Footer />
        </Container>
    );
};

export default Facilities;